import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import brandService, { Brand, BrandForm, GetBrandsParams } from '../services/brandService';
import { showAlert } from '../utils/alert';
import { getApiErrorMessage } from '../utils/mapFieldErrors';

// Hook สำหรับดึงรายการยี่ห้อ
export const useBrands = (params?: GetBrandsParams) => {
  return useQuery({
    queryKey: ['brands', params],
    queryFn: () => brandService.getBrands(params),
    staleTime: 5 * 60 * 1000, // 5 นาที
    gcTime: 10 * 60 * 1000, // 10 นาที (v5: renamed from cacheTime)
  });
};

// Hook สำหรับดึงยี่ห้อที่ active
// KD3 S2470: รับ enabled — หน้าที่ผู้ใช้อาจไม่มี brands.view ต้องไม่ยิง (เดิม 403 + console error ทุกครั้งที่เปิดหน้า)
export const useActiveBrands = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['brands', 'active'],
    queryFn: brandService.getActiveBrands,
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
};

// Hook สำหรับสร้างยี่ห้อใหม่
export const useCreateBrand = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: BrandForm) => brandService.createBrand(data),
    onSuccess: (newBrand) => {
      // อัพเดท cache ทันที
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      showAlert('สำเร็จ', 'สร้างยี่ห้อสำเร็จ', 'success');
    },
    onError: (error: any) => {
      const message = getApiErrorMessage(error, 'ไม่สามารถสร้างยี่ห้อได้');
      showAlert('เกิดข้อผิดพลาด', message, 'error');
    },
  });
};

// Hook สำหรับอัพเดทยี่ห้อ
export const useUpdateBrand = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<BrandForm> }) => 
      brandService.updateBrand(id, data),
    onSuccess: (updatedBrand) => {
      // อัพเดท cache ทันที
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.setQueryData(['brand', updatedBrand.id], updatedBrand);
      showAlert('สำเร็จ', 'แก้ไขยี่ห้อสำเร็จ', 'success');
    },
    onError: (error: any) => {
      const message = getApiErrorMessage(error, 'ไม่สามารถแก้ไขยี่ห้อได้');
      showAlert('เกิดข้อผิดพลาด', message, 'error');
    },
  });
};

// Hook สำหรับลบยี่ห้อ
export const useDeleteBrand = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => brandService.deleteBrand(id),
    onSuccess: (_, deletedId) => {
      // อัพเดท cache ทันที
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      showAlert('สำเร็จ', 'ลบยี่ห้อสำเร็จ', 'success');
    },
    onError: (error: any) => {
      const message = getApiErrorMessage(error, 'ไม่สามารถลบยี่ห้อได้');
      showAlert('เกิดข้อผิดพลาด', message, 'error');
    },
  });
};